import React, { useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import {
  Container,
  Content,
  GoBackArrow,
  GoForwardArrow,
  Label,
  MobileGoBack,
  Nav,
  NavItem
} from './Tabs.styled';
import { Heading } from 'components/v2/Typography/Heading';
import { useQueryParams } from 'hooks/useQueryParams';
import { useBreakpoints } from 'utilities/useBreakpoints';

import { Props } from './Tab.types';

export const Tabs = ({ tabs, tabUrlKey, onChangeTab, noMargin }: Props) => {
  const { isDesktop } = useBreakpoints();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useQueryParams();
  const activeTabId = queryParams.get(tabUrlKey);
  const activeTab = tabs.find(tab => tab.id === activeTabId);

  const changeTab = useCallback(
    (tabId?: string, replace?: boolean) => {
      const navigateOptions = replace ? { replace: true } : {};

      // clone existing search params
      const newUrlSearchParams = new URLSearchParams(queryParams);

      if (tabId && tabId !== activeTabId) {
        newUrlSearchParams.set(tabUrlKey, tabId);
        // add tab param to url but respect the existing query params
        navigate(`?${newUrlSearchParams}`, navigateOptions);
        onChangeTab?.(tabId);
      } else if (!tabId) {
        // this covers going back to the initial screen on mobile(no tab selected; tab navigation shown)
        // going back from an opened tab the tab param should be removed from url to show initial tab navigation screen
        newUrlSearchParams.delete(tabUrlKey);

        if (newUrlSearchParams.size) {
          // if there are any other search params keep them
          navigate(`?${newUrlSearchParams}`, navigateOptions);
        } else {
          // otherwise just update the url to remove all search parameters
          // Remove all search parameters while keeping the current pathname
          navigate(location.pathname, navigateOptions);
        }

        onChangeTab?.(tabId);
      }
    },
    [activeTabId, navigate, location, onChangeTab, queryParams, tabUrlKey]
  );

  useEffect(() => {
    const firstTab = tabs[0]?.id;

    if (!firstTab) return;

    if (isDesktop && !activeTab) changeTab(firstTab, true);
  }, [isDesktop, activeTab, tabs, changeTab]);

  return (
    <Container>
      <Nav $selectedTab={!!activeTab}>
        {tabs.map(tab => (
          <NavItem
            $active={activeTab?.id === tab.id}
            key={tab.id}
            onClick={() => changeTab(tab.id)}
          >
            <Label $active={activeTab?.id === tab.id} isDesktop={isDesktop}>
              {tab.label}
            </Label>
            <GoForwardArrow />
          </NavItem>
        ))}
      </Nav>

      <MobileGoBack
        $selectedTab={!activeTab}
        onClick={() => changeTab(undefined)}
        $noMargin={noMargin}
      >
        <Heading tag="span" styledAs="h4">
          <GoBackArrow />
          {activeTab?.label}
        </Heading>
      </MobileGoBack>

      {activeTab && <Content key={activeTab.id}>{activeTab.content}</Content>}
    </Container>
  );
};
